import { isEmpty, split } from "lodash";
import { useRouter } from "next/router";
import { Button } from "primereact/button";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { useCompanies } from "../../../../common/api/useCompanies";
import usePermissions from "../../../../common/api/usePermissions";
import { Card } from "../../../../common/components/Card";
import { PermissionsGate } from "../../../../common/components/PermissionsGate";
import {
    ADMIN_ROUTES,
    COMPANY_ROUTES,
    PERMISSIONS,
} from "../../../../common/constants";
import { useRedirectStore } from "../../../../common/store/useRedirectStore";
import { useSetUpSpacesStore } from "../../../set-up-spaces-flow/store";
import { ActiveModeChip } from "../ActiveModeChip";
import { AddLocationButton } from "../AddLocationButton";
import { LocationSelection } from "../LocationSelection";
import { UserMenu } from "../UserMenu";

interface HeaderProps {
    onOpenMenuClick?: () => void;
    showMenuIcon?: boolean;
    onLogoutClick?: () => void;
}

const Header = (props: HeaderProps) => {
    const {
        onOpenMenuClick = () => {},
        showMenuIcon = false,
        onLogoutClick = () => {},
    } = props;

    const router = useRouter();
    const { companyId, locationId } = router.query as {
        companyId?: string;
        locationId?: string;
    };

    const { lastLocationPath, setLastLocationPath } = useRedirectStore();

    const { toggleVisibility } = useSetUpSpacesStore();

    const { hasPermissions } = usePermissions();
    const { companies, isLoadingCompanies } = useCompanies();
    const hasCompany = isLoadingCompanies || companies?.[0];
    const showActiveModeChip =
        !!locationId &&
        hasPermissions({
            permissions: [PERMISSIONS.ENERGY_MODE.READ],
            companyId,
            locationId,
        });

    const handleSwitchBetterspaceView = (e: InputSwitchChangeEvent) => {
        if (e.value) {
            // Remove URL parameter before saving
            const splitRoute = split(router.asPath, "?");
            splitRoute?.[0] && setLastLocationPath(splitRoute?.[0]);
            router.push(ADMIN_ROUTES.SYSTEMCHECK);
            return;
        }
        if (!isEmpty(companies)) {
            if (lastLocationPath) {
                router.push({
                    pathname: lastLocationPath,
                });
                return;
            }
            // Until Company Dashboard page will be added
            const defaultCompanyRoute = COMPANY_ROUTES.LOCATIONS;
            router.push({
                pathname: defaultCompanyRoute,
                query: {
                    companyId: companies[0].id,
                },
            });
        }
    };

    return (
        <Card
            className="z-10 px-5 py-2 shadow-md md:px-10  mx-8 rounded-t-none"
            pt={{
                body: {
                    className: "!p-0",
                },
                content: {
                    className: "!p-0 flex justify-between md:justify-end",
                },
            }}
        >
            <div className="flex items-center">
                {showMenuIcon && (
                    <Button
                        className="md:hidden text-default"
                        icon="pi pi-bars"
                        rounded
                        text
                        aria-label="Open Sidebar"
                        onClick={onOpenMenuClick}
                    />
                )}
            </div>
            <div className="flex items-center gap-2">
                {showActiveModeChip && <ActiveModeChip className="mr-2" />}
                {/* Hide companies dropdown in Betterspace Admin view */}
                {(hasPermissions() && !companyId) || !hasCompany ? null : (
                    <LocationSelection />
                )}

                {!hasCompany && !hasPermissions() && (
                    <button onClick={toggleVisibility}>
                        <i className="pi pi-plus-circle text-xl"></i>
                    </button>
                )}
                {/* Add Company/Location button and Betterspace Admin switch are hidden in mobile view */}
                <div className="flex items-center gap-2">
                    <AddLocationButton />
                    <PermissionsGate>
                        <InputSwitch
                            checked={!companyId}
                            onChange={handleSwitchBetterspaceView}
                            inputId="bs-switch"
                        />
                        <label
                            htmlFor="bs-switch"
                            className="font-semibold hidden md:flex"
                        >
                            Betterspace Admin
                        </label>
                    </PermissionsGate>
                </div>

                <UserMenu onLogoutClick={onLogoutClick} />
            </div>
        </Card>
    );
};

export default Header;
